import { useCallback, useMemo } from "react";
import ReactFlow, {
  Node,
  Edge,
  MiniMap,
  Controls,
  Background,
  BackgroundVariant,
  NodeProps,
  Handle,
  Position,
} from "reactflow";
import "reactflow/dist/style.css";

interface Module {
  id: string;
  title: string;
  description: string;
  order: number;
  prerequisites: string[];
}

interface CourseProgress {
  courseId: string;
  completedModules: string[];
  startedAt: string;
  lastAccessedAt: string;
}

interface CourseRoadmapProps {
  modules: Module[];
  progress: CourseProgress;
  selectedModuleId: string | null;
  onModuleClick: (moduleId: string) => void;
  canvasHeight: number;
}

type ModuleStatus = "locked" | "available" | "in_progress" | "completed";

function getModuleStatus(
  mod: Module,
  completedModules: string[],
  selectedModuleId: string | null
): ModuleStatus {
  if (completedModules.includes(mod.id)) return "completed";
  const prereqsMet =
    mod.prerequisites.length === 0 ||
    mod.prerequisites.every((p) => completedModules.includes(p));
  if (!prereqsMet) return "locked";
  if (selectedModuleId === mod.id) return "in_progress";
  return "available";
}

const STATUS_STYLES: Record<
  ModuleStatus,
  { bg: string; border: string; text: string }
> = {
  locked: { bg: "#d9ddd8", border: "#94a29e", text: "#66777b" },
  available: { bg: "#f7f1e4", border: "#6f8d95", text: "#123046" },
  in_progress: { bg: "#f2e2c7", border: "#d4a54f", text: "#123046" },
  completed: { bg: "#dde9e0", border: "#4e7c74", text: "#1d2b2f" },
};

const STATUS_ICONS: Record<ModuleStatus, string> = {
  locked: "\u{1F512}",
  available: "\u{25CB}",
  in_progress: "\u{25D4}",
  completed: "\u2713",
};

function RoadmapNode({ data }: NodeProps) {
  const status: ModuleStatus = data.status;
  const style = STATUS_STYLES[status];
  return (
    <div
      className={`roadmap-node node-${status}`}
      style={{
        background: style.bg,
        borderColor: style.border,
        color: style.text,
        position: "relative",
      }}
    >
      <Handle type="target" position={Position.Top} style={{ opacity: 0 }} />
      <div className="node-status-icon">{STATUS_ICONS[status]}</div>
      <div className="node-order">Module {data.order}</div>
      <div className="node-title">{data.label}</div>
      <div className="node-desc">{data.description}</div>
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0 }} />
    </div>
  );
}

const nodeTypes = { roadmapNode: RoadmapNode };

export default function CourseRoadmap({
  modules,
  progress,
  selectedModuleId,
  onModuleClick,
  canvasHeight,
}: CourseRoadmapProps) {
  const sorted = useMemo(
    () => [...modules].sort((a, b) => a.order - b.order),
    [modules]
  );

  const nodes: Node[] = useMemo(
    () =>
      sorted.map((mod, i) => {
        const status = getModuleStatus(
          mod,
          progress.completedModules,
          selectedModuleId
        );
        return {
          id: mod.id,
          type: "roadmapNode",
          position: { x: 150, y: i * 300 },
          data: {
            label: mod.title,
            description: mod.description,
            order: mod.order,
            status,
          },
        };
      }),
    [sorted, progress.completedModules, selectedModuleId]
  );

  const edges: Edge[] = useMemo(
    () =>
      sorted.slice(1).map((mod, i) => {
        const prevMod = sorted[i];
        const targetStatus = getModuleStatus(
          mod,
          progress.completedModules,
          selectedModuleId
        );
        return {
          id: `e-${prevMod.id}-${mod.id}`,
          source: prevMod.id,
          target: mod.id,
          type: "default",
          animated: targetStatus === "available",
          style: {
            stroke:
              targetStatus === "completed"
                ? "#4e7c74"
                : targetStatus === "locked"
                  ? "#94a29e"
                  : "#6f8d95",
            strokeWidth: 2,
          },
        };
      }),
    [sorted, progress.completedModules, selectedModuleId]
  );

  const onInit = useCallback(
    (reactFlowInstance: any) => {
      if (sorted.length > 0) {
        setTimeout(() => {
          reactFlowInstance.fitView({
            padding: 0.18,
            duration: 600,
            maxZoom: 1.08,
          });
        }, 200);
      }
    },
    [sorted]
  );

  const onNodeClick = useCallback(
    (_: any, node: Node) => {
      const mod = modules.find((m) => m.id === node.id);
      if (!mod) return;
      const status = getModuleStatus(
        mod,
        progress.completedModules,
        selectedModuleId
      );
      if (status !== "locked") {
        onModuleClick(mod.id);
      }
    },
    [modules, progress.completedModules, selectedModuleId, onModuleClick]
  );

  return (
    <div className="course-flow-container">
      <div
        className="course-flow-canvas"
        style={{ height: `${canvasHeight}px` }}
      >
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          onNodeClick={onNodeClick}
          onInit={onInit}
          minZoom={0.3}
          maxZoom={1.5}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
        >
          <Background variant={BackgroundVariant.Dots} gap={20} size={1} color="rgba(18,48,70,0.16)" />
          <MiniMap
            nodeColor={(node) => {
              const s = node.data?.status as ModuleStatus;
              return STATUS_STYLES[s]?.border || "#404547";
            }}
            maskColor="rgba(18,48,70,0.12)"
            style={{ background: "#fbf8f1", border: "1px solid rgba(18,48,70,0.12)" }}
          />
          <Controls />
        </ReactFlow>
      </div>
    </div>
  );
}
